/**
 * Methodology Builder Service
 * 
 * Handles secure communication with Azure Functions for the RAG-powered
 * Methodology Generator system.
 * 
 * Security features:
 * - Environment-based configuration
 * - Function key authentication
 * - Error sanitization (no sensitive data leakage)
 * - HTTPS-only communication
 * - Client-side PDF text extraction for reliability
 */

import axios, { AxiosError } from 'axios';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker - use known stable CDN version
// Using jsdelivr with correct .mjs extension
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

// Configuration from environment variables
// Default to local API (/api) for Azure Static Web App deployment
// Handle empty string explicitly as it's falsy but not undefined
const envApiUrl = process.env.REACT_APP_METHODOLOGY_API_URL;
const METHODOLOGY_API_URL = (envApiUrl && envApiUrl.trim()) || '/api';
const METHODOLOGY_FUNCTION_KEY = process.env.REACT_APP_METHODOLOGY_FUNCTION_KEY || '';

// Debug logging
console.log('[Methodology Service] API URL configured:', METHODOLOGY_API_URL);
console.log('[Methodology Service] Environment variable:', envApiUrl);

// Log if using external API
if (METHODOLOGY_API_URL !== '/api') {
    console.log('Using external Methodology API:', METHODOLOGY_API_URL);
}

// Types - Updated to match actual Azure Function response structure
export interface TorExtractionResult {
    project_title: string;
    client_organization: string;
    programme_background: string;
    evaluation_purpose: string;
    methodology_requirements: {
        required_methods: string[];
        recommended_methods: string[];
        required_frameworks: string[];
        approach_type: string;
        specific_techniques: string[];
    };
    evaluation_questions: string[];
    scope: {
        geographic: string;
        timeframe: string;
        target_populations: string[];
        case_study_countries: string[];
    };
    sampling_requirements: {
        sample_description: string;
        stakeholder_groups: string[];
        minimum_numbers: string;
    };
    quality_requirements: {
        frameworks: string[];
        standards: string;
    };
    deliverables: string[];
    constraints: {
        timeline: string;
        budget: string;
        team_requirements: string;
        language_requirements: string[];
    };
    special_requirements: {
        gender_responsive: boolean | null;
        ethical_clearance: boolean | null;
        inclusion_focus: string;
        participatory_approaches: string;
    };
    key_success_factors: string[];
    query_vector?: number[]; // 1536-dimension embedding vector for similarity search
    full_tor_text?: string; // Full ToR text for chat context
}

export interface SimilarProposal {
    proposal_id: number;
    proposal_name: string;
    client: string;
    similarity_score: number;
    methodology_text: string;
    year?: number;
}

export interface MethodologyGenerationResult {
    generated_methodology: string;
    recommended_methods: string[];
    recommended_frameworks: string[];
    key_considerations: string[];
    error?: string;
    // Legacy fields for backwards compatibility
    methodology_text?: string;
    word_count?: number;
    generation_time?: number;
}

export interface GenerationProgress {
    stage: 'extracting' | 'searching' | 'generating' | 'complete' | 'error';
    message: string;
    progress: number; // 0-100
}

export interface RequirementAssessment {
    category: string;
    requirement_text: string;
    status: 'met' | 'partial' | 'missing';
    evidence: string;
    missing_details: string;
    recommendations: string;
    weight: number;
}

export interface MethodologyAssessmentResult {
    overall_score: number; // 0-100 percentage
    requirements_assessed: RequirementAssessment[];
    achieved_requirements: RequirementAssessment[];
    missing_requirements: RequirementAssessment[];
    total_requirements: number;
    requirements_met: number;
    error?: string;
}

/**
 * Sanitize error messages to prevent information leakage
 */
function sanitizeError(error: any): string {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;

        // Network errors
        if (!axiosError.response) {
            return 'Unable to connect to the Methodology Builder service. Please check your connection.';
        }

        // HTTP errors
        const status = axiosError.response.status;
        if (status === 401 || status === 403) {
            return 'Authentication failed. Please ensure you are logged in.';
        }
        if (status === 404) {
            return 'The requested service endpoint was not found.';
        }
        if (status === 429) {
            return 'Too many requests. Please wait a moment and try again.';
        }
        if (status >= 500) {
            return 'The Methodology Builder service is temporarily unavailable. Please try again later.';
        }

        // Client errors - provide general message
        return 'An error occurred while processing your request. Please try again.';
    }

    // Generic error
    return 'An unexpected error occurred. Please try again.';
}

/**
 * Create axios instance with authentication
 */
const createAuthenticatedClient = () => {
    return axios.create({
        baseURL: METHODOLOGY_API_URL,
        headers: {
            'Content-Type': 'application/json',
            ...(METHODOLOGY_FUNCTION_KEY && { 'x-functions-key': METHODOLOGY_FUNCTION_KEY })
        },
        timeout: 120000 // 2 minutes for long-running operations
    });
};

/**
 * Extract text from PDF with proper spacing between text items
 */
async function extractTextFromPDF(file: File): Promise<string> {
    try {
        const arrayBuffer = await file.arrayBuffer();

        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const textPages: string[] = [];

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();

            // Extract text with proper spacing
            let lastY = -1;
            let pageText = '';

            for (const item of textContent.items) {
                const textItem = item as any;

                // Add line break if Y position changed significantly (new line)
                if (lastY !== -1 && Math.abs(textItem.transform[5] - lastY) > 5) {
                    pageText += '\n';
                }

                // Add space before text if not at start of line
                if (pageText.length > 0 && !pageText.endsWith('\n') && !pageText.endsWith(' ')) {
                    pageText += ' ';
                }

                pageText += textItem.str;
                lastY = textItem.transform[5];
            }

            textPages.push(pageText);
        }

        const fullText = textPages.join('\n\n');
        console.log(`PDF extraction: ${fullText.length} characters from ${pdf.numPages} pages`);
        console.log('First 500 chars:', fullText.substring(0, 500));
        console.log('Last 500 chars:', fullText.substring(Math.max(0, fullText.length - 500)));

        return fullText;
    } catch (error) {
        console.error('PDF extraction failed:', error);
        throw new Error('Failed to extract text from PDF. The file may be corrupted or password-protected.');
    }
}

/**
 * Upload and extract ToR requirements
 * 
 * @param file - PDF or TXT file containing Terms of Reference
 * @returns Extracted requirements and metadata
 */
export async function uploadTor(file: File): Promise<TorExtractionResult> {
    // Validate file
    const allowedTypes = ['application/pdf', 'text/plain'];
    if (!allowedTypes.includes(file.type)) {
        throw new Error('Only PDF and TXT files are supported.');
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB.');
    }

    try {
        let torText: string;

        if (file.type === 'text/plain') {
            // Read text file
            torText = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsText(file);
            });
        } else if (file.type === 'application/pdf') {
            // Extract text from PDF client-side with improved spacing
            console.log('Extracting text from PDF...');
            torText = await extractTextFromPDF(file);

            if (!torText || torText.trim().length < 100) {
                throw new Error('PDF extraction failed or returned insufficient text. The PDF may be scanned/image-based or corrupted.');
            }
        } else {
            throw new Error('Unsupported file type. Only PDF and TXT files are supported.');
        }

        console.log(`Sending ${torText.length} characters to Azure Function for analysis...`);

        const payload = {
            tor_text: torText
        };

        const client = createAuthenticatedClient();
        const response = await client.post<TorExtractionResult>('extract-tor', payload);

        console.log('ToR extraction response:', response.data);

        // Ensure we have a query_vector for the next step
        if (!response.data.query_vector) {
            console.warn('Warning: query_vector not returned from tor-extractor. This may cause issues with vector search.');
        }

        // Attach the full ToR text to the response for chat context
        return {
            ...response.data,
            full_tor_text: torText
        };
    } catch (error) {
        console.error('ToR extraction failed:', error);
        throw new Error(sanitizeError(error));
    }
}

/**
 * Find similar proposals based on ToR query vector or requirements
 * 
 * @param queryVectorOrRequirements - Query vector from tor-extractor or requirements object
 * @param topK - Number of similar proposals to return (default: 5)
 * @returns List of similar proposals with similarity scores
 */
export async function findSimilarProposals(
    queryVectorOrRequirements: number[] | any,
    topK: number = 5
): Promise<SimilarProposal[]> {
    try {
        const client = createAuthenticatedClient();

        // vector-search requires query_vector (array of numbers)
        if (!Array.isArray(queryVectorOrRequirements)) {
            throw new Error('vector-search requires query_vector (array), but received non-array input');
        }

        const payload = {
            query_vector: queryVectorOrRequirements,
            top_k: topK
        };

        console.log('Vector search payload:', { vector_length: queryVectorOrRequirements.length, top_k: topK });

        const response = await client.post<{ results: SimilarProposal[] }>('vector-search', payload);

        console.log('Vector search response:', response.data);

        return response.data.results || [];
    } catch (error) {
        console.error('Similar proposal search failed:', error);
        throw new Error(sanitizeError(error));
    }
}

/**
 * Generate tailored methodology using RAG
 * 
 * @param torId - ID/title of the extracted ToR
 * @param torExtraction - Complete ToR extraction result
 * @param similarProposals - List of similar proposals to use for generation
 * @param filename - Original ToR filename
 * @returns Generated methodology text and metadata
 */
export async function generateMethodology(
    torId: string,
    torExtraction: TorExtractionResult,
    similarProposals: SimilarProposal[],
    filename: string = 'tor.pdf'
): Promise<MethodologyGenerationResult> {
    try {
        const client = createAuthenticatedClient();
        const response = await client.post<MethodologyGenerationResult>('generate-methodology', {
            filename: filename,
            tor_requirements: torExtraction,
            similar_methodologies: similarProposals.map(p => ({
                proposal_name: p.proposal_name,
                methodology_text: p.methodology_text,
                similarity_score: p.similarity_score
            }))
        });

        console.log('Methodology generation response:', response.data);

        // Check for errors in response
        if (response.data.error) {
            throw new Error(`Methodology generation failed: ${response.data.error}`);
        }

        // Check if methodology was actually generated
        if (!response.data.generated_methodology || response.data.generated_methodology.trim() === '') {
            throw new Error('Methodology generation returned empty result. Please try again with a more detailed ToR.');
        }

        return response.data;
    } catch (error) {
        console.error('Methodology generation failed:', error);
        throw new Error(sanitizeError(error));
    }
}

/**
 * Full pipeline: Upload ToR → Find similar → Generate methodology
 * 
 * @param file - ToR file
 * @param onProgress - Progress callback
 * @returns Generated methodology
 */
export async function generateMethodologyFromTor(
    file: File,
    onProgress?: (progress: GenerationProgress) => void
): Promise<{
    extraction: TorExtractionResult;
    similarProposals: SimilarProposal[];
    methodology: MethodologyGenerationResult;
    fullTorText: string;
}> {
    try {
        // Stage 1: Extract ToR
        onProgress?.({
            stage: 'extracting',
            message: 'Analysing Terms of Reference...',
            progress: 10
        });

        const extraction = await uploadTor(file);

        onProgress?.({
            stage: 'extracting',
            message: 'ToR requirements extracted successfully',
            progress: 30
        });

        // Stage 2: Find similar proposals
        onProgress?.({
            stage: 'searching',
            message: 'Searching for similar past proposals...',
            progress: 40
        });

        // vector-search requires query_vector, not text
        if (!extraction.query_vector) {
            throw new Error('ToR extraction did not return query_vector. Cannot search for similar proposals.');
        }

        const similarProposals = await findSimilarProposals(extraction.query_vector);

        onProgress?.({
            stage: 'searching',
            message: `Found ${similarProposals.length} similar proposals`,
            progress: 50
        });

        // Stage 3: Generate methodology
        onProgress?.({
            stage: 'generating',
            message: 'Generating tailored methodology...',
            progress: 60
        });

        const methodology = await generateMethodology(
            extraction.project_title || 'Untitled ToR', // Use project_title as ID
            extraction, // Pass entire extraction object as tor_requirements
            similarProposals,
            file.name // Pass filename to backend
        );

        onProgress?.({
            stage: 'complete',
            message: 'Methodology generated successfully',
            progress: 100
        });

        return {
            extraction,
            similarProposals,
            methodology,
            fullTorText: extraction.full_tor_text || ''
        };
    } catch (error) {
        onProgress?.({
            stage: 'error',
            message: error instanceof Error ? error.message : 'An error occurred',
            progress: 0
        });
        throw error;
    }
}

/**
 * Send a chat message to the methodology AI assistant
 * 
 * @param message - User's question
 * @param context - Full context including ToR, requirements, and methodology
 * @param mode - Chat mode: 'tor' (ToR-specific) or 'research' (general evaluation research)
 * @returns AI assistant's response
 */
export async function sendChatMessage(
    message: string,
    context?: string,
    mode: 'tor' | 'research' = 'tor'
): Promise<{ response: string; timestamp: string }> {
    try {
        const client = createAuthenticatedClient();
        const response = await client.post<{ response: string; timestamp: string }>(
            'chatfunction',
            {
                message,
                context,
                mode
            }
        );

        return response.data;
    } catch (error) {
        console.error('Chat message failed:', error);
        throw new Error(sanitizeError(error));
    }
}

/**
 * Assess a finished methodology against ToR requirements
 * 
 * @param methodologyText - The finished methodology text to assess
 * @param torExtraction - The extracted ToR requirements from uploadTor()
 * @param torSegmentText - Optional: Specific ToR segment text to compare against
 * @returns Assessment result with percentage met, achieved requirements, and missing items
 */
export async function assessMethodology(
    methodologyText: string,
    torExtraction: TorExtractionResult,
    torSegmentText?: string
): Promise<MethodologyAssessmentResult> {
    if (!methodologyText || methodologyText.trim().length === 0) {
        throw new Error('Methodology text is required.');
    }

    if (!torExtraction || Object.keys(torExtraction).length === 0) {
        throw new Error('ToR extraction is required. Please upload and extract ToR first.');
    }

    try {
        const client = createAuthenticatedClient();
        const response = await client.post<MethodologyAssessmentResult>(
            'assess-methodology',
            {
                methodology_text: methodologyText,
                tor_extraction: torExtraction,
                ...(torSegmentText && { tor_segment_text: torSegmentText })
            }
        );

        console.log('Methodology assessment response:', response.data);

        // Check for errors in response
        if (response.data.error) {
            throw new Error(`Methodology assessment failed: ${response.data.error}`);
        }

        // Ensure overall_score is valid
        if (typeof response.data.overall_score !== 'number' || isNaN(response.data.overall_score)) {
            throw new Error('Invalid assessment result: overall_score is missing or invalid.');
        }

        return response.data;
    } catch (error) {
        console.error('Methodology assessment failed:', error);
        throw new Error(sanitizeError(error));
    }
}

/**
 * Check if Methodology Builder service is configured
 */
export function isMethodologyBuilderConfigured(): boolean {
    // Local API (/api) is always configured - it's part of the Static Web App
    // External API would need a function key
    return Boolean(METHODOLOGY_API_URL);
}

